// FIXME
// eslint-disable-next-line workspaces/require-dependency
import { type DefaultRootState } from '@databases/redux/default-root-state';
import {
  type Database,
  type DatabaseField,
  DatabaseFieldType,
  type DatabaseValue,
} from '@databases/types/database';
import { type DatabaseViewSettings } from '@databases/types/database/view';

import {
  getNumberRepresentation,
  getStringRepresentation,
  isEmptyValue,
} from '../field-types';
import { getReferencedJiraStatusCategory } from '../field-types/jiraIssueDetails';
import { getConvertedDatabaseValue, getDatabaseEntryRequired } from '../utils';

type SortWithField = DatabaseViewSettings['sorts'][number] & {
  field: DatabaseField;
};

export const isSortWithField = (
  maybeSortWithField: DatabaseViewSettings['sort'] & { field?: DatabaseField }
): maybeSortWithField is SortWithField => {
  return maybeSortWithField.field !== undefined;
};

/**
 * Compares two strings or numbers and returns a negative number if the second one is greater, a positive number if the
 * first one is greater, or zero if both are equal.
 * If both arguments are numbers (in number or string form), they are compared numerically. Otherwise, they are compared
 * naturally (alphabetical segments are compared alphabetically, numerical segments are compared numerically).
 *
 * One implication of this for now is that pure numbers support decimals, negatives and thousands separators, while number
 * segments in mixed values don't. For example, "-10" is less than "-2", while "JIRA-10" is greater than "JIRA-2" (despite
 * the minus sign in both cases). Also, "1.10" is less than "1.2", while "JIRA-1.10" is greater than "JIRA-1.2" (since in
 * the mixed value the number segments are compared separately).
 *
 * Following international standards, a full stop and a comma are supported as decimal separators, and a space and thin space
 * (along with their non-breaking variants) are supported as thousands separators. Full-stops and commas are not supported
 * as thousands separators, as they are impossible to distinguish from decimal separators.
 */
export function compareStringOrNumber(
  numberOrStringA: string | number,
  numberOrStringB: string | number,
  ascending: boolean
): number {
  const [a, b] = ascending
    ? [numberOrStringA, numberOrStringB]
    : [numberOrStringB, numberOrStringA];
  const [numberA, numberB] = [a, b].map((numberOrString) =>
    typeof numberOrString === 'number'
      ? numberOrString
      : Number(
          numberOrString
            .trim()
            .replace(/[,]/g, '.') // Normalize decimal separator
            .replace(/[\u2212]/g, '-') // Normalize minus (Unicode minus)
            .replace(/[ \u2009\u202F\xa0']/g, '') // Remove thousands separators (space, thin space, non-breaking thin space, non-breaking space, apostrophe)
        )
  );

  if (!isNaN(numberA) && !isNaN(numberB)) {
    return numberA - numberB;
  } else {
    return `${a}`.localeCompare(`${b}`, 'en-US', { caseFirst: 'upper', numeric: true });
  }
}

type SelectOptionIndexMap = {
  indexMap: Map<string, number>;
  fallbackIndex: number;
};

/**
 * Builds a lookup map from select option id/label to its position index.
 */
function buildSelectOptionIndexMap(field: DatabaseField): SelectOptionIndexMap {
  const selectOptions = field.settings.selectOptions;
  const indexMap = new Map<string, number>();
  selectOptions.forEach((option, index) => {
    if (option.id) {
      indexMap.set(option.id, index);
    }
    indexMap.set(option.label, index);
  });
  return { indexMap, fallbackIndex: selectOptions.length };
}

/**
 * Compares two select field values by the order of their options in field.settings.selectOptions.
 * For multi-select, the minimum option index among selected values determines the sort position.
 * Options not found in selectOptions are treated as having an index after all known options.
 */
function compareByCustomOrder(
  valueA: DatabaseValue,
  valueB: DatabaseValue,
  selectOptionIndexMap: SelectOptionIndexMap,
  ascending: boolean
): number {
  const { indexMap, fallbackIndex } = selectOptionIndexMap;

  const getMinIndex = (databaseValue: DatabaseValue): number => {
    const tags = databaseValue.value;
    if (!Array.isArray(tags) || tags.length === 0) {
      return fallbackIndex;
    }
    let minIndex = fallbackIndex;
    for (const tag of tags) {
      if (typeof tag !== 'string') {
        continue;
      }
      const idx = indexMap.get(tag) ?? fallbackIndex;
      if (idx < minIndex) {
        minIndex = idx;
      }
    }
    return minIndex;
  };

  const indexA = getMinIndex(valueA);
  const indexB = getMinIndex(valueB);

  const diff = ascending ? indexA - indexB : indexB - indexA;
  return diff;
}

const STATUS_CATEGORY_ORDER: Record<string, number> = {
  'to-do': 0,
  'in-progress': 1,
  done: 2,
};
const STATUS_CATEGORY_FALLBACK_INDEX = 3;

/**
 * Compares two entries by the status category of their referenced Jira issues.
 * Uses the canonical Jira status category ordering: to-do → in-progress → done.
 * Entries with unknown or missing status categories are sorted after all known categories.
 */
function compareByStatusCategory(
  entryIdA: string,
  entryIdB: string,
  sortField: DatabaseField,
  databaseDraft: Database,
  state: DefaultRootState | undefined,
  ascending: boolean
): number {
  const getStatusCategoryIndex = (entryId: string): number => {
    const entry = getDatabaseEntryRequired(databaseDraft, entryId);
    const statusCategory = getReferencedJiraStatusCategory(
      { database: databaseDraft, field: sortField, entry },
      state
    );

    return statusCategory
      ? STATUS_CATEGORY_ORDER[statusCategory] ?? STATUS_CATEGORY_FALLBACK_INDEX
      : STATUS_CATEGORY_FALLBACK_INDEX;
  };

  const indexA = getStatusCategoryIndex(entryIdA);
  const indexB = getStatusCategoryIndex(entryIdB);
  return ascending ? indexA - indexB : indexB - indexA;
}

type EntrySortContext<Db extends Database> = {
  databaseDraft: Db;
  sortsWithField: SortWithField[];
  state: DefaultRootState | undefined;
  selectOptionIndexMaps: Map<string, SelectOptionIndexMap>;
};

const compareEntries = <Db extends Database>(
  entryIdA: string,
  entryIdB: string,
  context: EntrySortContext<Db>
): number => {
  const { databaseDraft, sortsWithField, state } = context;

  if (sortsWithField.length === 0) {
    return 0;
  }

  const sortWithField = sortsWithField[0];
  const sortField = sortWithField.field;

  const entryA = getDatabaseEntryRequired(databaseDraft, entryIdA);
  const entryB = getDatabaseEntryRequired(databaseDraft, entryIdB);
  const contextA = {
    database: databaseDraft,
    field: sortField,
    entry: entryA,
    value: getConvertedDatabaseValue(databaseDraft, sortField, entryA, state),
  };
  const contextB = {
    database: databaseDraft,
    field: sortField,
    entry: entryB,
    value: getConvertedDatabaseValue(databaseDraft, sortField, entryB, state),
  };

  // Ignore empty values when sorting
  const isEmptyValueA = isEmptyValue(contextA, state);
  const isEmptyValueB = isEmptyValue(contextB, state);
  if (isEmptyValueA && isEmptyValueB) {
    return compareEntries(entryIdA, entryIdB, {
      ...context,
      sortsWithField: sortsWithField.slice(1),
    });
  } else if (isEmptyValueA) {
    return 1;
  } else if (isEmptyValueB) {
    return -1;
  }

  let comparison: number;

  if (sortWithField.customOrder && sortField.type === DatabaseFieldType.SELECT) {
    const selectOptionIndexMap =
      context.selectOptionIndexMaps.get(sortField.id) ??
      buildSelectOptionIndexMap(sortField);
    comparison = compareByCustomOrder(
      contextA.value,
      contextB.value,
      selectOptionIndexMap,
      sortWithField.ascending
    );
  } else if (
    sortWithField.customOrder &&
    sortField.type === DatabaseFieldType.JIRA_ISSUE_DETAILS &&
    sortField.settings.jiraField === 'status'
  ) {
    comparison = compareByStatusCategory(
      entryIdA,
      entryIdB,
      sortField,
      databaseDraft,
      state,
      sortWithField.ascending
    );
  } else {
    comparison = compareStringOrNumber(
      getNumberRepresentation(contextA, state) ??
        (getStringRepresentation(contextA, state) || ''),
      getNumberRepresentation(contextB, state) ??
        (getStringRepresentation(contextB, state) || ''),
      sortWithField.ascending
    );
  }

  return comparison === 0
    ? compareEntries(entryIdA, entryIdB, {
        ...context,
        sortsWithField: sortsWithField.slice(1),
      })
    : comparison;
};

export function sortEntriesMulti<Db extends Database>(
  databaseDraft: Db,
  entries: string[],
  sortsWithField: SortWithField[],
  state: DefaultRootState | undefined,
  ignoredEntries: string[] = []
) {
  const entriesToSort = entries.filter((entry) => !ignoredEntries.includes(entry));
  const ignoredEntriesWithIndexes = entries
    .map((entry, index) => ({ entry, index }))
    .filter(({ entry }) => ignoredEntries.includes(entry));

  const selectOptionIndexMaps = new Map<string, SelectOptionIndexMap>();
  for (const sort of sortsWithField) {
    if (sort.customOrder && sort.field.type === DatabaseFieldType.SELECT) {
      selectOptionIndexMaps.set(sort.field.id, buildSelectOptionIndexMap(sort.field));
    }
  }

  const sortedEntries = entriesToSort.sort((entryIdA, entryIdB) => {
    return compareEntries(entryIdA, entryIdB, {
      databaseDraft,
      sortsWithField,
      state,
      selectOptionIndexMaps,
    });
  });

  // Reinsert ignored entries in their original positions
  ignoredEntriesWithIndexes.forEach(({ entry, index }) => {
    sortedEntries.splice(index, 0, entry);
  });

  return sortedEntries;
}
